import { useEffect, useMemo, useState } from "react";
import { authService } from "../../services/api";
import { useNotification } from "../Notifications/NotificationContext";
import "./PushNotifications.css";

function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
        .replace(/-/g, "+")
        .replace(/_/g, "/");

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
}

function isMobile() {
    return /android|iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isStandalone() {
    if (window.navigator.standalone) return true;
    if (window.matchMedia("(display-mode: standalone)").matches) return true;
    return false;
}

export default function PushNotifications({ showHelp: showHelpProp, setShowHelp: setShowHelpProp } = {}) {
    const { showError, showInfo, showSuccess } = useNotification();

    const [vapidKey, setVapidKey] = useState("");
    const [isSupported, setIsSupported] = useState(true);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    // showHelp pode ser controlado pelo pai (botão no footer do Profile) ou
    // localmente (fallback, ex.: abrir o guia no mobile via handleToggle).
    const [showHelpLocal, setShowHelpLocal] = useState(false);
    const showHelp = showHelpProp !== undefined ? showHelpProp : showHelpLocal;
    const setShowHelp = setShowHelpProp || setShowHelpLocal;
    const [showBotUsername, setShowBotUsername] = useState(false);
    const [showPlanName, setShowPlanName] = useState(false);
    const [loadingPref, setLoadingPref] = useState(false);

    const canUsePush = useMemo(() => {
        return "serviceWorker" in navigator && "PushManager" in window;
    }, []);

    const getRegistration = async () => {
        if (!canUsePush) return null;

        try {
            const existing = await navigator.serviceWorker.getRegistration("/");
            if (existing) return existing;

            const registration = await navigator.serviceWorker.register("/service_worker.js", {
                scope: "/",
                updateViaCache: "none"
            });

            await navigator.serviceWorker.ready;
            console.log("[PushNotifications] Service Worker registrado com sucesso");

            registration.onupdatefound = () => {
                const installingWorker = registration.installing;
                if (!installingWorker) return;
                installingWorker.onstatechange = () => {
                    if (installingWorker.state === "installed" && navigator.serviceWorker.controller) {
                        console.log("[PushNotifications] Nova versão do Service Worker instalada");
                    }
                };
            };

            return registration;
        } catch (err) {
            console.error("[PushNotifications] Erro ao registrar Service Worker:", err);
            return null;
        }
    };

    const refreshSubscriptionState = async () => {
        if (!canUsePush) {
            setIsSupported(false);
            return;
        }

        try {
            const registration = await getRegistration();
            if (!registration) {
                console.warn("[PushNotifications] Service Worker não pôde ser registrado");
                return;
            }

            const subscription = await registration.pushManager.getSubscription();
            setIsSubscribed(!!subscription);
        } catch (err) {
            console.warn("[PushNotifications] Erro ao verificar subscription:", err.message);
        }
    };

    const handleSubscribe = async () => {
        if (!("Notification" in window)) {
            showError("Notificações não suportadas neste navegador.");
            return;
        }

        if (!vapidKey) {
            showError("Chave VAPID não configurada.");
            return;
        }

        try {
            console.log("[PushNotifications] Solicitando permissão...");
            const permission = await Notification.requestPermission();
            console.log("[PushNotifications] Permissão:", permission);

            if (permission !== "granted") {
                showInfo("Permissão para notificações negada.");
                setIsSubscribed(false);
                return;
            }

            console.log("[PushNotifications] Obtendo registration...");
            const registration = await getRegistration();
            if (!registration) {
                showError("Service Worker indisponível neste navegador.");
                return;
            }

            console.log("[PushNotifications] Inscrevendo no pushManager...");
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(vapidKey)
            });

            console.log("[PushNotifications] Subscription obtida:", subscription);
            console.log("[PushNotifications] Subscription endpoint:", subscription.endpoint);

            const subscriptionJSON = subscription.toJSON();
            console.log("[PushNotifications] Enviando ao backend:", subscriptionJSON);

            await authService.push.subscribe(subscriptionJSON);
            setIsSubscribed(true);
            showSuccess("Notificações ativadas neste dispositivo!");
        } catch (err) {
            console.error("[PushNotifications] Erro ao ativar:", err);
            console.error("[PushNotifications] Erro stack:", err.stack);
            console.error("[PushNotifications] Erro response:", err.response?.data);

            const errorMsg = err.response?.data?.error || err.message || "Erro desconhecido";
            showError(`Erro ao ativar notificações: ${errorMsg}`);
            setIsSubscribed(false);
        }
    };

    const handleUnsubscribe = async () => {
        try {
            const registration = await getRegistration();
            if (!registration) {
                setIsSubscribed(false);
                return;
            }

            const subscription = await registration.pushManager.getSubscription();
            if (!subscription) {
                setIsSubscribed(false);
                return;
            }

            await authService.push.unsubscribe(subscription.endpoint);
            await subscription.unsubscribe();
            setIsSubscribed(false);
            showInfo("Notificações desativadas neste dispositivo.");
        } catch (err) {
            console.error("[PushNotifications] Erro ao desativar:", err);
            showError("Erro ao desativar notificações.");
        }
    };

    const handleToggle = async (event) => {
        const nextChecked = event.target.checked;

        if (nextChecked && isMobile() && !isStandalone()) {
            setShowHelp(true);
            setIsSubscribed(false);
            return;
        }

        setIsLoading(true);

        try {
            if (nextChecked) {
                await handleSubscribe();
            } else {
                await handleUnsubscribe();
            }
        } catch (err) {
            console.warn("[PushNotifications] Toggle error:", err);
            showError("Erro ao atualizar notificações.");
            await refreshSubscriptionState();
        } finally {
            setIsLoading(false);
        }
    };

    const handleShowBotUsernameToggle = async (e) => {
        const checked = e.target.checked;
        setLoadingPref(true);
        try {
            await authService.push.updatePreferences({ push_show_bot_username: checked });
            setShowBotUsername(checked);
            showSuccess(checked ? "@ do bot ativado nas notificações!" : "@ do bot removido das notificações.");
        } catch (err) {
            console.warn("[PushNotifications] Erro ao salvar preferência:", err);
            showError("Erro ao salvar preferência.");
        } finally {
            setLoadingPref(false);
        }
    };

    const handleShowPlanNameToggle = async (e) => {
        const checked = e.target.checked;
        setLoadingPref(true);
        try {
            await authService.push.updatePreferences({ push_show_plan_name: checked });
            setShowPlanName(checked);
            showSuccess(checked ? "Nome do plano ativado nas notificações!" : "Nome do plano removido das notificações.");
        } catch (err) {
            console.warn("[PushNotifications] Erro ao salvar preferência:", err);
            showError("Erro ao salvar preferência.");
        } finally {
            setLoadingPref(false);
        }
    };

    useEffect(() => {
        setIsSupported(canUsePush);

        const bootstrap = async () => {
            setIsLoading(true);
            try {
                const response = await authService.profile();
                setVapidKey(response.data?.vapid_public_key || "");
                setShowBotUsername(Boolean(response.data?.push_show_bot_username));
                setShowPlanName(Boolean(response.data?.push_show_plan_name));
            } catch (err) {
                console.warn("[PushNotifications] Erro ao carregar VAPID:", err);
            } finally {
                setIsLoading(false);
            }
        };

        bootstrap();
    }, [canUsePush]);

    useEffect(() => {
        if (!canUsePush) return;
        refreshSubscriptionState();
    }, [canUsePush]);

    return (
        <section className="push-section">



            <div className="ob-card">
                <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <i className="fas fa-bell text-[#3b82f6] text-sm" aria-hidden="true" />
                            <h3 className="font-semibold text-white text-sm">Ativar Notificações neste dispositivo</h3>
                        </div>
                        <label className="toggle-wrapper toggle-sm">
                            <input
                                type="checkbox"
                                checked={isSubscribed}
                                onChange={handleToggle}
                                disabled={!isSupported || isLoading}
                            />
                            <span className="toggle-slider" />
                        </label>
                    </div>
                    <p className="text-[11px] text-white/40">
                        Receba alertas de vendas neste dispositivo.
                    </p>

                    {isSubscribed && (
                        <div className="mt-3 pt-3 border-t border-white/10 space-y-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <i className="fas fa-at text-white/50 text-xs w-4 text-center" aria-hidden="true" />
                                    <span className="text-[13px] text-white/80">Mostrar @ do bot nas notificações</span>
                                </div>
                                <label className="toggle-wrapper toggle-sm">
                                    <input
                                        type="checkbox"
                                        checked={showBotUsername}
                                        onChange={handleShowBotUsernameToggle}
                                        disabled={loadingPref}
                                    />
                                    <span className="toggle-slider" />
                                </label>
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <i className="fas fa-tag text-white/50 text-xs w-4 text-center" aria-hidden="true" />
                                    <span className="text-[13px] text-white/80">Mostrar nome do plano nas notificações</span>
                                </div>
                                <label className="toggle-wrapper toggle-sm">
                                    <input
                                        type="checkbox"
                                        checked={showPlanName}
                                        onChange={handleShowPlanNameToggle}
                                        disabled={loadingPref}
                                    />
                                    <span className="toggle-slider" />
                                </label>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* {!isSupported && (
                <div className="push-warning">
                    Seu navegador não suporta notificações push ou você está em HTTP (use HTTPS ou localhost).
                </div>
            )} */}

            {showHelp && (
                <div className="push-modal" onClick={() => setShowHelp(false)}>
                    <div className="push-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            className="push-modal-close"
                            onClick={() => setShowHelp(false)}
                        >
                            &times;
                        </button>

                        <div className="push-modal-head">
                            <span className="push-modal-badge">
                                <i className="fas fa-bell" />
                            </span>
                            <div>
                                <h3>Como ativar notificações</h3>
                                <p className="push-modal-subtitle">
                                    Adicione o app à tela inicial do celular para receber alertas de vendas.
                                </p>
                            </div>
                        </div>

                        <div className="push-guide">
                            <div className="push-guide-col">
                                <div className="push-guide-head push-guide-head--ios">
                                    <i className="fab fa-apple" />
                                    <span>iPhone / iPad</span>
                                </div>
                                <ol className="push-steps">
                                    <li>
                                        <span className="push-step-icon"><i className="fab fa-safari" /></span>
                                        <span className="push-step-text">Abra este site no <strong>Safari</strong>.</span>
                                    </li>
                                    <li>
                                        <span className="push-step-icon"><i className="fas fa-arrow-up-from-bracket" /></span>
                                        <span className="push-step-text">Toque no botão de <strong>compartilhar</strong>.</span>
                                    </li>
                                    <li>
                                        <span className="push-step-icon"><i className="fas fa-square-plus" /></span>
                                        <span className="push-step-text">Escolha <strong>“Adicionar à Tela de Início”</strong>.</span>
                                    </li>
                                    <li>
                                        <span className="push-step-icon"><i className="fas fa-check" /></span>
                                        <span className="push-step-text">Confirme tocando em <strong>“Adicionar”</strong>.</span>
                                    </li>
                                    <li>
                                        <span className="push-step-icon"><i className="fas fa-mobile-screen-button" /></span>
                                        <span className="push-step-text">Abra o app pela <strong>tela inicial</strong>.</span>
                                    </li>
                                    <li>
                                        <span className="push-step-icon"><i className="fas fa-bell" /></span>
                                        <span className="push-step-text">Volte em <strong>Meu Perfil</strong> e ative as notificações.</span>
                                    </li>
                                </ol>
                            </div>

                            <div className="push-guide-divider" />

                            <div className="push-guide-col">
                                <div className="push-guide-head push-guide-head--android">
                                    <i className="fab fa-android" />
                                    <span>Android</span>
                                </div>
                                <ol className="push-steps">
                                    <li>
                                        <span className="push-step-icon"><i className="fab fa-chrome" /></span>
                                        <span className="push-step-text">Abra este site no <strong>Chrome</strong>.</span>
                                    </li>
                                    <li>
                                        <span className="push-step-icon"><i className="fas fa-ellipsis-vertical" /></span>
                                        <span className="push-step-text">Toque no menu de <strong>três pontos</strong>.</span>
                                    </li>
                                    <li>
                                        <span className="push-step-icon"><i className="fas fa-download" /></span>
                                        <span className="push-step-text">Selecione <strong>“Instalar app”</strong> ou “Adicionar à tela inicial”.</span>
                                    </li>
                                    <li>
                                        <span className="push-step-icon"><i className="fas fa-check" /></span>
                                        <span className="push-step-text">Confirme tocando em <strong>“Instalar”</strong>.</span>
                                    </li>
                                    <li>
                                        <span className="push-step-icon"><i className="fas fa-mobile-screen-button" /></span>
                                        <span className="push-step-text">Abra o app pela <strong>tela inicial</strong>.</span>
                                    </li>
                                    <li>
                                        <span className="push-step-icon"><i className="fas fa-bell" /></span>
                                        <span className="push-step-text">Volte em <strong>Meu Perfil</strong> e ative as notificações.</span>
                                    </li>
                                </ol>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
